import { useCallback } from 'react';
import { Platform } from 'react-native';
import * as Application from 'expo-application';
import * as Device from 'expo-device';
import { nanoid as uuid } from 'nanoid';

import { AsyncStorage } from '@pletly/common';
import { client, unauthenticatedClient, getUserId } from '@pletly/feature-auth';
import { DeviceType, WebPushSubscriptionInput } from '%__generated__/types';
import { getPushSubscription } from './getPushSubscription';
import { useClearPushTokenMutation } from './__generated__/clearPushToken.mutation.generated';
import { useUpdatePushtokenMutation } from './__generated__/updatePushToken.mutation.generated';

export const usePushToken = () => {
	const [updatePushToken] = useUpdatePushtokenMutation({ client });
	const [clearPushTokenMutation] = useClearPushTokenMutation({ client: unauthenticatedClient });

	const savePushToken = useCallback(async () => {
		const userId = await getUserId();
		if (!userId) return;

		try {
			const [pushTokens, deviceInfo] = await Promise.all([getPushSubscription(), getDeviceInfo()]);

			await updatePushToken({
				variables: {
					userId,
					deviceInfo: {
						...deviceInfo,
						expoPushToken: pushTokens?.expoPushToken,
						webPushSubscription: pushTokens?.pushSubscription
							? (pushTokens?.pushSubscription as unknown as WebPushSubscriptionInput)
							: undefined,
					},
				},
			});
		} catch (error) {
			console.error(error);
		}
	}, [updatePushToken]);

	const clearPushToken = useCallback(async () => {
		const [userId, deviceId] = await Promise.all([getUserId('ClearPushToken'), getDeviceId()]);
		if (!userId || !deviceId) {
			return;
		}

		await clearPushTokenMutation({ variables: { deviceId, userId } });
	}, [clearPushTokenMutation]);

	return { savePushToken, clearPushToken };
};
async function getDeviceInfo() {
	const [deviceId, type] = await Promise.all([getDeviceId(), getDeviceType()]);
	const { deviceName, osName, osVersion } = Device;
	const appVersion = Application.nativeApplicationVersion;

	return { deviceId, type, deviceName, osName, osVersion, appVersion };
}

async function getDeviceId() {
	if (Platform.OS === 'ios') {
		return (await Application.getIosIdForVendorAsync()) || getGeneratedDeviceId();
	}

	if (Platform.OS === 'android') {
		return Application.getAndroidId() || getGeneratedDeviceId();
	}

	return getGeneratedDeviceId();
}

async function getGeneratedDeviceId() {
	const DEVICE_ID_STORAGE_KEY = 'device_id';
	const savedDeviceId = await AsyncStorage.getString(DEVICE_ID_STORAGE_KEY);
	if (!savedDeviceId) {
		const newDeviceId = uuid();
		await AsyncStorage.setStorageItem(DEVICE_ID_STORAGE_KEY, newDeviceId);

		return newDeviceId;
	}

	return savedDeviceId;
}

async function getDeviceType() {
	const device = await Device.getDeviceTypeAsync();
	if (device === Device.DeviceType.UNKNOWN) {
		return;
	}

	return Device.DeviceType[device] as DeviceType;
}
